import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PublicNav from "../../components/layout/PublicNav";
import PublicFooter from "../../components/layout/PublicFooter";
import SMSRecruiterCard, { SMS_COPY } from "../../components/SMSRecruiterCard";
import PageMeta from "../../components/seo/PageMeta";
import {
  ArrowRight, ArrowUpRight, CheckCircle2, Sparkles, ExternalLink,
  Plane, MapPin, CalendarClock, Microscope, Dumbbell, Stethoscope,
  Crown, Video, FileSignature, UserCheck,
  Building2, Hospital, HeartPulse, Home, Activity, Shield,
  Gauge, Network, BadgeCheck, Clock, Users, HeartHandshake,
} from "lucide-react";

/* Editorial image used in the hero split */
const HERO_IMG = "https://images.pexels.com/photos/7089401/pexels-photo-7089401.jpeg";

/* ------------------------------------------------------------------ */
/*  10 service cards — each with overview / who / value / CTA + link  */
/* ------------------------------------------------------------------ */

const SERVICES = [
  {
    Icon: Plane,
    category: "Contingent · Travel",
    title: "Travel Nursing",
    overview: "13-week and longer travel RN contracts across the U.S., with transparent bill-rate math and rapid deployment.",
    who: "Health systems absorbing census surges, seasonal demand, or open-unit roles while recruiting.",
    value: "Pre-credentialed RNs deployed in under 7 days · Compact-license fast-tracking · One dedicated account team.",
    cta: { label: "Explore Travel Nursing", to: "/services/travel-nursing" },
  },
  {
    Icon: MapPin,
    category: "Contingent · Local",
    title: "Local Staffing",
    overview: "Commute-friendly clinical contracts from 4–13 weeks, governed by each facility's specific onboarding rules.",
    who: "Facilities that want in-market clinicians without housing stipends or tax-home complexity.",
    value: "W-2 hourly with overtime · Local depth in 40+ metros · Faster onboarding via facility-specific rule sets.",
    cta: { label: "Explore Local Staffing", to: "/services/local-per-diem-staffing#local" },
  },
  {
    Icon: CalendarClock,
    category: "Contingent · PRN",
    title: "Per Diem Staffing",
    overview: "Same-week, shift-by-shift coverage through a credentialed open-shift marketplace — governed by your rule set.",
    who: "Operators needing responsive fill for callouts, holidays, and census spikes without long-lead contracts.",
    value: "Credentialed pool, no re-submission loops · Same-day fill in top metros · Tamper-evident audit trail per shift.",
    cta: { label: "Explore Per Diem Staffing", to: "/services/local-per-diem-staffing#per-diem" },
  },
  {
    Icon: Microscope,
    category: "Allied Health",
    title: "Allied Health Staffing",
    overview: "Lab, imaging, respiratory, cardiac, and surgical-tech professionals for inpatient and outpatient settings.",
    who: "Hospitals, reference labs, imaging centers, and ambulatory networks with specialty coverage gaps.",
    value: "Coverage across 20+ allied specialties · Modality-specific competency verification · Nationwide reach.",
    cta: { label: "Explore Allied Health", to: "/services/allied-health-staffing" },
  },
  {
    Icon: Dumbbell,
    category: "Therapy Services",
    title: "Therapy Staffing",
    overview: "PT, OT, SLP, PTA, and COTA placements across acute, inpatient rehab, SNF, home health, and schools.",
    who: "Rehab leaders balancing productivity targets with quality of therapy hours.",
    value: "Specialty-matched therapists · Rehab-specific skills checklists · Home-health route density support.",
    cta: { label: "Explore Therapy Staffing", to: "/services/therapy-staffing" },
  },
  {
    Icon: Stethoscope,
    category: "Provider",
    title: "Physician & APP Staffing",
    overview: "MD, DO, NP, and PA placements for hospitalist, ED, specialty, locum, and permanent roles.",
    who: "Medical staff offices, locum departments, and health-system physician recruiters.",
    value: "Credentialing and privileging assistance · Malpractice coordination · Specialty-aware sourcing.",
    cta: { label: "Explore Physician & APP", to: "/services/physician-app-staffing" },
  },
  {
    Icon: Crown,
    category: "Leadership",
    title: "Executive Leadership",
    overview: "Interim and direct-hire placements for CNO, CMO, directors of nursing, quality, and ambulatory operations.",
    who: "Boards and CEOs bridging leadership gaps, turnarounds, or planned transitions.",
    value: "Senior clinical operators ready in days · Search-retainer flexibility · Executive reference depth.",
    cta: { label: "Explore Executive Leadership", to: "/services/executive-leadership" },
  },
  {
    Icon: Video,
    category: "Virtual Care",
    title: "Telehealth Staffing",
    overview: "Virtual-care clinicians for tele-ICU, tele-behavioral health, virtual urgent care, and specialty consult.",
    who: "Health systems, telehealth platforms, and value-based-care programs scaling virtual supply.",
    value: "Multi-state licensed clinicians · HIPAA-aware workflows · Remote onboarding tooling.",
    cta: { label: "Explore Telehealth", to: "/services/telehealth-staffing" },
  },
  {
    Icon: FileSignature,
    category: "Workforce Programs",
    title: "Contract Staffing",
    overview: "13–26 week facility-defined contracts with dedicated account management and candidate-submission controls.",
    who: "Programs formalizing contingent spend under MSA rate cards and SLAs.",
    value: "Transparent rate card · Submission volume & quality SLAs · VMS-ready integration.",
    cta: { label: "Explore Contract Staffing", to: "/services/contract-direct-hire#contract" },
  },
  {
    Icon: UserCheck,
    category: "Permanent",
    title: "Direct Hire",
    overview: "Permanent clinical placement with sourcing, screening, and offer-stage support — fee-for-hire or retained.",
    who: "Talent-acquisition leaders shifting from contingent reliance to sustainable full-time hiring.",
    value: "90-day replacement guarantee · Competency-verified candidates · Salary-band intelligence.",
    cta: { label: "Explore Direct Hire", to: "/services/contract-direct-hire#direct-hire" },
  },
];

/* ------------------------------------------------------------------ */
/*  Supporting industries / facility types                             */
/* ------------------------------------------------------------------ */

const INDUSTRIES = [
  { Icon: Hospital,   t: "Acute-care hospitals",          d: "Level I–IV trauma, community, critical-access." },
  { Icon: Building2,  t: "Health systems & IDNs",          d: "Multi-facility operators and MSPs." },
  { Icon: HeartPulse, t: "Ambulatory & specialty clinics", d: "Cardiology, oncology, infusion, surgical centers." },
  { Icon: Home,       t: "SNF, LTAC & home health",        d: "Post-acute operators and at-home networks." },
  { Icon: Activity,   t: "Behavioral & psychiatric",       d: "Inpatient, PHP, IOP, and crisis services." },
  { Icon: Shield,     t: "Government, VA & correctional",  d: "Federal, state, municipal, and DoD contracts." },
];

/* ------------------------------------------------------------------ */
/*  Why the TRITAL delivery model works                                */
/* ------------------------------------------------------------------ */

const WHY = [
  { Icon: Gauge,    t: "One intake, multi-model fulfillment", d: "A single requisition can be scoped as travel, local, per diem, or direct hire — we advise on the best fit." },
  { Icon: Network,  t: "VMS-ready from day one",               d: "We plug into Aya, Medefis, SHIFT, RightSourcing, and custom VMS instances without custom engineering." },
  { Icon: BadgeCheck, t: "Credentialing discipline",            d: "Primary-source verification, facility rule sets, document expiry tracking — with a tamper-evident audit trail." },
  { Icon: Clock,    t: "Reliability over cleverness",           d: "Transparent rates, dependable fill, no hidden markups. Procurement clears us on first review." },
  { Icon: Users,    t: "Clinician-first economics",             d: "On-screen pay math, portable credentials, and recruiters that stay with clinicians across assignments." },
  { Icon: HeartHandshake, t: "Accountable partnership",          d: "One executive sponsor per account, monthly program reviews with CNO and scheduling leaders." },
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                             */
/* ------------------------------------------------------------------ */

const fadeUp = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.4, ease: [0.16, 0.84, 0.44, 1] },
};

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

export default function Services() {
  return (
    <div className="min-h-screen bg-white" data-testid="services-page">
      <PageMeta
        title="Workforce Services — Nursing, Allied, Therapy | TRITAL Care®"
        description="Every clinical discipline, one workforce system. Travel, local, per diem, allied, therapy, physician & APP, executive, telehealth, contract and direct-hire services."
        canonical="https://www.tritalcare.com/services"
      />
      <PublicNav />

      {/* 1 · HERO */}
      <section className="relative overflow-hidden bg-trital-mesh" data-testid="services-hero">
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 pt-14 pb-16 lg:pt-20 lg:pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div {...fadeUp} className="lg:col-span-7">
            <nav aria-label="Breadcrumb" className="text-xs text-slate-500 mb-5">
              <Link to="/" className="hover:text-trital-navy">Home</Link>
              <span className="mx-2 text-slate-300">/</span>
              <span className="text-slate-700">Services</span>
            </nav>
            <div className="overline text-trital-red">Workforce Services</div>
            <h1 className="mt-3 font-display font-black text-[44px] leading-[1.02] sm:text-5xl lg:text-[72px] lg:leading-[1.02] tracking-tight text-slate-900 text-balance">
              Every clinical discipline.
              <span className="block text-trital-navy">One workforce system.</span>
            </h1>
            <p className="mt-6 text-[16px] lg:text-[17px] text-slate-600 max-w-xl leading-relaxed">
              Ten service lines, one delivery model. Whether you need a 13-week travel RN, a same-week PRN shift filled,
              or a permanent CNO placed — TRITAL Care® runs one intake, one compliance framework, and one executive
              sponsor across it all.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/register?role=facility" className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-trital-navy text-white font-semibold btn-lift hover:bg-trital-navy-deep shadow-[0_10px_30px_-10px_rgba(19,39,122,0.45)]" data-testid="services-hero-request">
                Request Talent <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.8} />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-slate-300 text-trital-navy font-semibold hover:border-trital-navy hover:bg-slate-50 btn-lift" data-testid="services-hero-contact">
                Talk to our team
              </Link>
              <a href="#services-directory" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-trital-navy px-3 py-2" data-testid="services-hero-jump">
                Jump to service directory <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.8} />
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 max-w-md gap-6">
              {[
                { v: "10",   l: "Service lines, one intake" },
                { v: "50",   l: "States covered, W-2 payroll" },
                { v: "<48h", l: "Typical shortlist turnaround" },
              ].map((s) => (
                <div key={s.l}>
                  <div className="font-display font-black text-3xl lg:text-4xl text-trital-navy tabular-nums">{s.v}</div>
                  <div className="mt-1 text-[12px] uppercase tracking-[0.18em] text-slate-500 leading-tight">{s.l}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-navy">
              <img src={HERO_IMG} alt="Clinical operations team reviewing workforce schedule" className="w-full h-[420px] lg:h-[500px] object-cover" style={{ filter: "saturate(0.92) contrast(1.02)" }} />
              <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-trital-navy-ink/70 via-trital-navy-ink/10 to-transparent" />
              <div className="absolute left-5 right-5 bottom-5 glass-card rounded-2xl p-4 bg-white/94">
                <div className="overline text-trital-red">What facilities get</div>
                <div className="mt-1 font-display font-bold text-slate-900 text-[15px] leading-snug">
                  One executive sponsor. One compliance framework. Every clinical staffing model.
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2 · SERVICES DIRECTORY (10 cards) */}
      <section id="services-directory" className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24 scroll-mt-24" data-testid="services-directory">
        <div className="max-w-[760px] mb-12">
          <div className="overline text-trital-red">Service Directory</div>
          <h2 className="mt-3 font-display font-black text-3xl lg:text-[44px] leading-[1.08] text-slate-900 text-balance">
            Ten services, built on one compliance spine.
          </h2>
          <p className="mt-4 text-slate-600 max-w-[60ch]">
            Pick the model that fits today's need. Or talk to us — we'll advise you into the right mix across contingent,
            permanent, and project-based engagements.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {SERVICES.map((svc, i) => {
            const external = svc.cta.external;
            const LinkComp = external ? "a" : Link;
            const linkProp = external
              ? { href: svc.cta.to, target: "_blank", rel: "noopener noreferrer" }
              : { to: svc.cta.to };
            const idSlug = svc.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
            return (
              <motion.article
                key={svc.title}
                {...fadeUp}
                transition={{ duration: 0.4, delay: (i % 2) * 0.06 }}
                className="card-premium card-premium-hover rounded-3xl p-7 lg:p-8 group h-full flex flex-col"
                data-testid={`service-card-${idSlug}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center shrink-0 group-hover:bg-trital-navy group-hover:text-white transition-colors">
                    <svc.Icon className="w-5 h-5" strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0">
                    <div className="overline text-slate-500">{svc.category}</div>
                    <h3 className="mt-1 font-display font-bold text-[20px] text-slate-900 leading-snug">{svc.title}</h3>
                  </div>
                </div>
                <p className="mt-5 text-[14px] text-slate-700 leading-relaxed">{svc.overview}</p>

                <div className="mt-5 space-y-3 text-[13.5px]">
                  <div>
                    <div className="overline text-slate-500">Who it serves</div>
                    <p className="mt-1 text-slate-700 leading-relaxed">{svc.who}</p>
                  </div>
                  <div>
                    <div className="overline text-slate-500">Value</div>
                    <p className="mt-1 text-slate-700 leading-relaxed">{svc.value}</p>
                  </div>
                </div>

                <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between">
                  <LinkComp
                    {...linkProp}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-trital-navy hover:gap-2 transition-all"
                    aria-label={external ? `${svc.cta.label} (opens in new tab)` : undefined}
                    data-testid={`service-cta-${idSlug}`}
                  >
                    {svc.cta.label}
                    {external ? <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.8} /> : <ArrowRight className="w-4 h-4" strokeWidth={1.8} />}
                  </LinkComp>
                  <Link
                    to="/register?role=facility"
                    className="text-[12px] text-slate-500 hover:text-trital-red font-semibold"
                    data-testid={`service-request-${idSlug}`}
                  >
                    Request Talent →
                  </Link>
                </div>
              </motion.article>
            );
          })}
        </div>
      </section>

      {/* 3 · SUPPORTING INDUSTRIES / FACILITY TYPES */}
      <section className="bg-trital-mist border-y border-slate-200" data-testid="services-industries">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24">
          <div className="max-w-[720px] mb-12">
            <div className="overline text-trital-red">Industries served</div>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-[44px] leading-[1.08] text-slate-900 text-balance">
              Built for every setting where clinical care happens.
            </h2>
            <p className="mt-4 text-slate-600 max-w-[60ch]">
              The same delivery model works across acute, post-acute, ambulatory, behavioral, and government settings —
              because the spine is compliance, credentialing, and clinical matching.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {INDUSTRIES.map(({ Icon, t, d }, i) => (
              <motion.div
                key={t}
                {...fadeUp}
                transition={{ duration: 0.35, delay: (i % 3) * 0.05 }}
                className="card-premium rounded-2xl p-6"
              >
                <div className="w-11 h-11 rounded-xl bg-trital-navy text-white flex items-center justify-center">
                  <Icon className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <div className="mt-5 font-display font-bold text-[17px] text-slate-900">{t}</div>
                <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">{d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 · WHY OUR DELIVERY MODEL WORKS */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24" data-testid="services-why">
        <div className="max-w-[720px] mb-12">
          <div className="overline text-trital-red">Why it works</div>
          <h2 className="mt-3 font-display font-black text-3xl lg:text-[44px] leading-[1.08] text-slate-900 text-balance">
            Why the TRITAL Care® delivery model is different.
          </h2>
          <p className="mt-4 text-slate-600 max-w-[60ch]">
            Most staffing companies are organized around selling hours. We're organized around delivering compliant
            clinical capacity — the way operations and procurement leaders actually need it.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {WHY.map(({ Icon, t, d }, i) => (
            <motion.div
              key={t}
              {...fadeUp}
              transition={{ duration: 0.35, delay: (i % 3) * 0.05 }}
              className="card-premium card-premium-hover rounded-2xl p-6"
            >
              <div className="w-11 h-11 rounded-xl bg-red-50 text-trital-red flex items-center justify-center">
                <Icon className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div className="mt-5 font-display font-bold text-lg text-slate-900">{t}</div>
              <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">{d}</p>
            </motion.div>
          ))}
        </div>

        {/* Quick-action band */}
        <div className="mt-12 rounded-3xl bg-white border border-slate-200 shadow-card p-6 lg:p-8 flex flex-col lg:flex-row gap-6 lg:items-center lg:justify-between">
          <div className="max-w-xl">
            <div className="overline text-trital-red">Already have a need?</div>
            <div className="mt-2 font-display font-bold text-xl text-slate-900">Skip the scroll. Share the scope and we'll respond in one business day.</div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/register?role=facility" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-trital-navy text-white text-sm font-semibold btn-lift hover:bg-trital-navy-deep" data-testid="services-midcta-request">
              Request Talent <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
            <Link to="/employers" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-slate-300 text-trital-navy text-sm font-semibold hover:border-trital-navy" data-testid="services-midcta-employers">
              Employers overview
            </Link>
          </div>
        </div>
      </section>

      {/* 5 · FINAL CTA BAND */}
      <section className="relative overflow-hidden bg-trital-navy-mesh text-white" data-testid="services-cta-band">
        <div className="absolute inset-0 dot-grid opacity-10" aria-hidden="true" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24 grid lg:grid-cols-[1.3fr_1fr] items-center gap-10">
          <div>
            <div className="overline text-trital-red flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" strokeWidth={1.8} /> Ready when you are
            </div>
            <h3 className="mt-3 font-display font-black text-4xl lg:text-[52px] leading-[1.05] text-balance">
              One conversation. The right staffing model.
            </h3>
            <p className="mt-4 text-white/80 max-w-[58ch]">
              Tell us the scope. We'll propose the staffing model, rate framework, and SLA — typically within one business
              day. No auto-enrollment, no hidden markups, no exclusivity requirements.
            </p>
            <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[13px] text-white/70">
              <li className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} /> VMS-ready integration</li>
              <li className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} /> Procurement-ready paper</li>
              <li className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} /> Executive sponsor per account</li>
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:min-w-[320px]">
            <Link to="/register?role=facility" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white text-slate-900 text-sm font-semibold btn-lift hover:bg-slate-100" data-testid="services-final-cta-request">
              Request Talent <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-trital-red text-white text-sm font-semibold btn-lift hover:bg-trital-red-deep" data-testid="services-final-cta-demo">
              Book a demo <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
            <a href="/shiftbridge" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full border border-white/25 text-white text-sm font-semibold hover:bg-white/10" aria-label="PRN Shifts (opens in new tab)" data-testid="services-final-cta-prn">
              PRN Shifts <ExternalLink className="w-4 h-4" strokeWidth={1.8} />
            </a>
            <Link to="/clinicians" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full text-white/80 text-sm font-semibold hover:text-white" data-testid="services-final-cta-clinicians">
              For Clinicians →
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16" data-testid="services-sms">
        <SMSRecruiterCard variant="default" headline={SMS_COPY.services.headline} subtext={SMS_COPY.services.sub} />
      </section>

      <PublicFooter />
    </div>
  );
}
